'use client';

import { ProgressProvider } from '@bprogress/next/app';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
// import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { NuqsAdapter } from 'nuqs/adapters/next/app';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { useEffect, useState } from 'react';

import { HeaderProvider } from '@/components/global/header-provider';
import { ThemeProvider } from '@/components/theme/theme-provider';
import { Toaster } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { PRIMARY_BRAND_COLOR } from '@/lib/constants/brand';
import { env } from '@/lib/env';

const POSTHOG_DEFAULTS_VERSION = '2025-05-24';

export const Providers = ({ children }: { children: React.ReactNode }) => {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            staleTime: 1000 * 60,
          },
        },
      }),
  );

  useEffect(() => {
    if (env.NEXT_PUBLIC_ENVIRONMENT === 'development') return;
    if (posthog.__loaded) return;
    posthog.init(env.NEXT_PUBLIC_POSTHOG_KEY, {
      api_host: env.NEXT_PUBLIC_POSTHOG_HOST,
      ui_host: 'https://eu.posthog.com',
      defaults: POSTHOG_DEFAULTS_VERSION,
      person_profiles: 'identified_only',
      capture_pageleave: true,
      disable_session_recording: env.NEXT_PUBLIC_ENVIRONMENT !== 'production',
      loaded: (posthogClient) => {
        posthogClient.register({ environment: env.NEXT_PUBLIC_ENVIRONMENT });
      },
    });
  }, []);

  return (
    <PostHogProvider client={posthog}>
      <ThemeProvider>
        <NuqsAdapter>
          <QueryClientProvider client={queryClient}>
            {/* <ReactQueryDevtools initialIsOpen={false} /> */}
            <Toaster closeButton richColors />
            <ProgressProvider
              height="2px"
              color={PRIMARY_BRAND_COLOR}
              options={{ showSpinner: false }}
              shallowRouting
            >
              <TooltipProvider delayDuration={100}>
                <HeaderProvider>{children}</HeaderProvider>
              </TooltipProvider>
            </ProgressProvider>
          </QueryClientProvider>
        </NuqsAdapter>
      </ThemeProvider>
    </PostHogProvider>
  );
};
